import { ReactNode, useCallback, useEffect, useState } from 'react';
import { fetchTopics } from '@api/fetch-topics';
import { IMessage, INlpResponse, ITopic, ITopicGroup, ITopicTag, TopicStatus } from '@common/types';
import { AGREEMENT_BANNER_STORAGE_KEY } from '@constants/local-storage';
import useLocalStorage from '@hooks/use-local-storage';
import useRealPersonLiveChat from '@hooks/use-realperson-live-chat';
import { CURRENT_INSTANCE } from '@lib/utils';
import ChatProviderContext from '@stores/contexts/chat-context';

interface IChatProviderProps {
  children: ReactNode;
}

function ChatProvider({ children, ...props }: IChatProviderProps) {
  const [topics, setTopics] = useState<ITopic[]>([]);
  const [topicGroups, setTopicGroups] = useState<ITopicGroup[]>([]);
  const [topicTags, setTopicTags] = useState<ITopicTag[]>([]);
  const [selectedTopic, setSelectedTopic] = useState<string>('');
  const [messages, setMessages] = useState<IMessage[]>([]);
  const [responses, setResponses] = useState<Array<{ id: number; response: INlpResponse }>>([]);
  const [showAgreementBanner, setShowAgreementBanner] = useState<boolean>(false);
  const [agreedToDisclaimer, setAgreedToDisclaimer] = useLocalStorage<boolean>(
    AGREEMENT_BANNER_STORAGE_KEY,
    false
  );
  const [agreedToDisclaimerPerSession, setAgreedToDisclaimerPerSession] = useState<boolean>(false);
  const addMessage = useCallback(
    (message: IMessage) => setMessages((previousMessages) => [...previousMessages, message]),
    []
  );
  const liveChat = useRealPersonLiveChat({ messages, addMessage });

  useEffect(() => {
    if (topics.length === 0) {
      fetchTopics()
        .then((result) => {
          if (result) {
            let allowedTopics: ITopic[];
            if (CURRENT_INSTANCE.topics.length === 0) {
              allowedTopics = result.filter((topic) => TopicStatus.Inactive !== topic.status);
            } else {
              allowedTopics = result
                .filter((topic) => TopicStatus.Inactive !== topic.status)
                .filter((topic) => CURRENT_INSTANCE.topics.includes(topic.name.toLowerCase()));
            }

            setTopics(allowedTopics);

            const allTopicGroups: ITopicGroup[] = allowedTopics
              .filter((topic) => topic.groupId)
              .map((topic) => ({
                id: topic.groupId!,
                name: topic.groupName!,
                synonyms: topic.groupSynonyms,
              }));
            const uniqueTopicGroups = Array.from(
              new Map(allTopicGroups.map((item) => [item.id, item])).values()
            );
            setTopicGroups(uniqueTopicGroups);

            const allTopicTags = allowedTopics.flatMap((topic) => topic.tags || []);
            const uniqueTopicTags = Array.from(
              new Map(allTopicTags.map((item) => [item.id, item])).values()
            );
            setTopicTags(uniqueTopicTags);
          }
        })
        .catch(console.error);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const value = {
    topics,
    topicGroups,
    topicTags,
    selectedTopic,
    setSelectedTopic,
    messages,
    addMessage,
    responses,
    addResponse: (id: number, response: INlpResponse) =>
      setResponses((prev) => [...prev, { id, response }]),
    agreedToDisclaimer,
    setAgreedToDisclaimer,
    agreedToDisclaimerPerSession,
    setAgreedToDisclaimerPerSession,
    showAgreementBanner,
    setShowAgreementBanner,
    liveChat,
  };

  return (
    <ChatProviderContext.Provider {...props} value={value}>
      {children}
    </ChatProviderContext.Provider>
  );
}

export default ChatProvider;
